import { grpc } from '@improbable-eng/grpc-web'
import consola from 'consola'
import posthog from 'posthog-js'
import { type Observable, from, of, switchMap, throwError, timer } from 'rxjs'
import { catchError, retry } from 'rxjs/operators'
import type { GenericPayloadResponse, MultiGenericPayloadResponse } from '~/protos/gen/stcs_commons.entity'
import { StcsResCode } from '~/protos/gen/stcs_enums.entity'
import {
  type CompByOrgListRequest,
  type CompByUserListRequest,
  type CompCreateRequest,
  type CompDelRequest,
  type CompGetRequest,
  type CompUpdateI18nMapRequest,
  type CompUpdateRequest,
  StcsWappSvcCompcomp_by_org_listDesc,
  StcsWappSvcCompcomp_by_user_listDesc,
  StcsWappSvcCompcomp_createDesc,
  StcsWappSvcCompcomp_deleteDesc,
  StcsWappSvcCompcomp_getDesc,
  StcsWappSvcCompcomp_updateDesc,
  StcsWappSvcCompcomp_update_i18n_mapDesc,
} from '~/protos/gen/stcs_wapp_svc_comp'
import { type CompActualsResValuesGetRequest, type CompFssFlatGetRequest, StcsWappSvcCompFinancialscomp_actuals_res_values_getDesc, StcsWappSvcCompFinancialscomp_fss_flat_getDesc } from '~/protos/gen/stcs_wapp_svc_comp_financials'
import {
  type CompKpiCfgCalcRequest,
  type CompKpiCfgCreateRequest,
  type CompKpiResValuesGetRequest,
  StcsWappSvcCompKpicomp_kpi_cfg_calcDesc,
  StcsWappSvcCompKpicomp_kpi_cfg_createDesc,
  StcsWappSvcCompKpicomp_kpi_res_values_getDesc,
} from '~/protos/gen/stcs_wapp_svc_comp_kpi'
import {
  type CompPlanCfgActivateRequest,
  type CompPlanCfgCalcRequest,
  type CompPlanCfgCreateRequest,
  type CompPlanCfgDeactivateRequest,
  type CompPlanCfgDelRequest,
  type CompPlanCfgGetRequest,
  type CompPlanCfgListRequest,
  type CompPlanCfgSaveRequest,
  type CompPlanResValuesGetRequest,
  type CompPlanRfGetRequest,
  StcsWappSvcCompPlancomp_plan_cfg_activateDesc,
  StcsWappSvcCompPlancomp_plan_cfg_calcDesc,
  StcsWappSvcCompPlancomp_plan_cfg_createDesc,
  StcsWappSvcCompPlancomp_plan_cfg_deactivateDesc,
  StcsWappSvcCompPlancomp_plan_cfg_deleteDesc,
  StcsWappSvcCompPlancomp_plan_cfg_getDesc,
  StcsWappSvcCompPlancomp_plan_cfg_listDesc,
  StcsWappSvcCompPlancomp_plan_cfg_saveDesc,
  StcsWappSvcCompPlancomp_plan_res_values_getDesc,
  StcsWappSvcCompPlancomp_plan_rf_res_values_getDesc,
} from '~/protos/gen/stcs_wapp_svc_comp_plan'
import {
  type DmCompAccDataUploadConfirmRequest,
  type DmCompAccDataUploadRequest,
  type DmCompAcmConfirmRequest,
  type DmCompAcmGetDraftRequest,
  type DmCompAcmGetRequest,
  type DmCompAcmPreviewRequest,
  type DmCompAcmRestoreRequest,
  type DmCompAcmSaveDraftRequest,
  type DmCompDataOverViewRequest,
  type DmCompDeleteActualsRequest,
  type DmOrgFsdFlatConfirmRequest,
  type DmOrgFsdFlatGetDraftRequest,
  type DmOrgFsdFlatGetRequest,
  type DmOrgFsdFlatPreviewRequest,
  type DmOrgFsdFlatRestoreRequest,
  type DmOrgFsdFlatSaveDraftRequest,
  type DmOrgFsdTemplateApplyRequest,
  type DmOrgFsdTemplateListRequest,
  StcsWappSvcDataManagementdm_comp_acc_data_uploadDesc,
  StcsWappSvcDataManagementdm_comp_acc_data_upload_confirmDesc,
  StcsWappSvcDataManagementdm_comp_acm_confirmDesc,
  StcsWappSvcDataManagementdm_comp_acm_getDesc,
  StcsWappSvcDataManagementdm_comp_acm_get_draftDesc,
  StcsWappSvcDataManagementdm_comp_acm_previewDesc,
  StcsWappSvcDataManagementdm_comp_acm_restoreDesc,
  StcsWappSvcDataManagementdm_comp_acm_save_draftDesc,
  StcsWappSvcDataManagementdm_comp_data_overviewDesc,
  StcsWappSvcDataManagementdm_comp_delete_actualsDesc,
  StcsWappSvcDataManagementdm_org_fsd_flat_confirmDesc,
  StcsWappSvcDataManagementdm_org_fsd_flat_getDesc,
  StcsWappSvcDataManagementdm_org_fsd_flat_get_draftDesc,
  StcsWappSvcDataManagementdm_org_fsd_flat_previewDesc,
  StcsWappSvcDataManagementdm_org_fsd_flat_restoreDesc,
  StcsWappSvcDataManagementdm_org_fsd_flat_save_draftDesc,
  StcsWappSvcDataManagementdm_org_fsd_template_applyDesc,
  StcsWappSvcDataManagementdm_org_fsd_template_listDesc,
} from '~/protos/gen/stcs_wapp_svc_dm'
import {
  type OnboardingCompleteAccMappingRequest,
  type OnboardingCompleteFsdRequest,
  type OnboardingCompleteRequest,
  type OnboardingConfirmWelcomeRequest,
  type OnboardingPauseRequest,
  type OnboardingResumeRequest,
  StcsWappSvcOnboardingonboarding_completeDesc,
  StcsWappSvcOnboardingonboarding_complete_acc_mappingDesc,
  StcsWappSvcOnboardingonboarding_complete_data_uploadDesc,
  StcsWappSvcOnboardingonboarding_complete_fsdDesc,
  StcsWappSvcOnboardingonboarding_confirm_welcomeDesc,
  StcsWappSvcOnboardingonboarding_pauseDesc,
  StcsWappSvcOnboardingonboarding_resumeDesc,
} from '~/protos/gen/stcs_wapp_svc_onboarding'
import {
  type OrgCompSortListRequest,
  type OrgCreateRequest,
  type OrgGetRequest,
  type OrgListRequest,
  type OrgUpdateRequest,
  type OrgUserAddRequest,
  type OrgUserDelRequest,
  type OrgUserListRequest,
  type OrgUserUpdateAccessRequest,
  StcsWappSvcOrgorg_comp_sort_listDesc,
  StcsWappSvcOrgorg_createDesc,
  StcsWappSvcOrgorg_getDesc,
  StcsWappSvcOrgorg_listDesc,
  StcsWappSvcOrgorg_updateDesc,
  StcsWappSvcOrgorg_user_addDesc,
  StcsWappSvcOrgorg_user_delDesc,
  StcsWappSvcOrgorg_user_listDesc,
  StcsWappSvcOrgorg_user_update_accessDesc,
} from '~/protos/gen/stcs_wapp_svc_org'
import { type OrgPaymentStatusRequest, StcsWappSvcPaymentorg_check_payment_statusDesc } from '~/protos/gen/stcs_wapp_svc_payment.ts'
import {
  type GetWappSettingsRequest,
  StcsWappSvcSettingsget_org_wapp_settingsDesc,
  StcsWappSvcSettingsupdate_comp_user_wapp_settingsDesc,
  StcsWappSvcSettingsupdate_comp_wapp_settingsDesc,
  StcsWappSvcSettingsupdate_org_wapp_settingsDesc,
  type UpdateCompUserWappSettingsRequest,
  type UpdateCompWappSettingsRequest,
  type UpdateOrgWappSettingsRequest,
} from '~/protos/gen/stcs_wapp_svc_settings'
import { GrpcWebImpl, StcsWappSvcUseruser_deleteDesc, StcsWappSvcUseruser_getDesc, StcsWappSvcUseruser_updateDesc, type UserDeleteRequest, type UserGetRequest, type UserUpdateRequest } from '~/protos/gen/stcs_wapp_svc_user'
import { useAuthStore } from '~/stores/auth.store'
import { useNuxtApp, useRuntimeConfig } from '#imports'

let grpcWebImplInstance: GrpcWebImpl | null = null

function cmwClient(): GrpcWebImpl {
  if (!grpcWebImplInstance) {
    const runtimeConfig = useRuntimeConfig()
    grpcWebImplInstance = new GrpcWebImpl(runtimeConfig.public.cmwUrl, {})
  }
  return grpcWebImplInstance
}

/**
 * Makes a gRPC web request with auth token and response code validation.
 *
 * @param desc - The gRPC method descriptor
 * @param req - The request payload
 * @param allowedCodes - Array of valid response codes (default: [SUCCESS])
 * @param retryConfig - Retry configuration (default: {count: 100, delayMs: 1000})
 * @returns Observable<R> - Response observable
 *
 * @example
 * // Only accept SUCCESS response code
 * callCmwRequest(StcsWappSvcUsergetUserDesc, {
 *   ref_id_user: "user123"
 * })
 *
 * @example
 * // Accept both SUCCESS and NOT_FOUND codes
 * callCmwRequest(StcsWappSvcUsergetUserDesc, {
 *   ref_id_user: "user123"
 * }, [StcsResCode.SUCCESS, StcsResCode.NOT_FOUND])
 *
 * @example
 * // Accept SUCCESS with custom retry config
 * callCmwRequest(StcsWappSvcUsergetUserDesc, {
 *   ref_id_user: "user123"
 * }, [StcsResCode.SUCCESS], { count: 3, delayMs: 500 })
 */
function callCmwRequest<T extends grpc.ProtobufMessage, R extends grpc.ProtobufMessage>(
  desc: grpc.UnaryMethodDefinition<T, R>,
  req: T,
  allowedCodes: number[] = [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED],
  retryConfig = { count: 0, delayMs: 1000 },
): Observable<R> {
  const { $config, $clerk } = useNuxtApp()
  const authStore = useAuthStore()
  const startTime = Date.now()
  const requestContext = {
    service: desc.service.serviceName,
    methodName: desc.methodName,
    requestId: crypto.randomUUID(),
    payload: JSON.stringify(req).slice(0, 200),
    timestamp: new Date().toISOString(),
  }

  // Get Clerk session token and build metadata
  const getMetadata = async (): Promise<grpc.Metadata> => {
    const metadata = new grpc.Metadata()
    metadata.set('x-correlation-id', requestContext.requestId)

    // Add Clerk session token for backend authentication
    if ($clerk?.session) {
      const token = await $clerk.session.getToken()
      if (token) {
        metadata.set('authorization', `Bearer ${token}`)
      }
    }

    // Add Turnstile token for bot protection
    if (authStore.turnstileToken) {
      metadata.set('x-turnstile-token', authStore.turnstileToken)
    }

    return metadata
  }

  return from(getMetadata()).pipe(
    switchMap((metadata) => cmwClient().unary(desc, req, metadata)),
    catchError((err) => {
      const errorContext = {
        ...requestContext,
        errorType: 'NetworkError',
        duration: Date.now() - startTime,
        retryAttempt: 0,
        error: err.message,
      }

      consola.error('🔴 Request failed:', errorContext)
      posthog.captureException(`🔴 WAPP v${$config.public.appVersion} | cmw ${errorContext.methodName}| ${JSON.stringify(errorContext)}`)
      return throwError(() => err)
    }),
    switchMap((res) => {
      if (!allowedCodes.includes(res.res_code)) {
        const errorContext = {
          ...requestContext,
          errorType: 'BusinessError',
          responseCode: res.res_code,
          responseMessage: res.res_message,
          duration: Date.now() - startTime,
        }

        consola.error('🔴 Invalid response code:', errorContext)
        posthog.captureException(`🔴 WAPP v${$config.public.appVersion} | cmw ${errorContext.methodName}| ${JSON.stringify(errorContext)}`)
        return throwError(() => new Error(`ErrorCode: ${res.res_code}, ErrorMessage: ${res.res_message}`))
      }

      posthog.capture('grpc_request_success', {
        ...requestContext,
        duration: Date.now() - startTime,
      })
      return of(res)
    }),
    retry({
      count: retryConfig.count,
      delay: (error, retryCount) => {
        posthog.capture('grpc_request_retry', {
          ...requestContext,
          retryCount,
          error: error.message,
        })
        return timer(retryConfig.delayMs)
      },
    }),
    catchError((err) => {
      const errorContext = {
        ...requestContext,
        errorType: 'RetryExhausted',
        duration: Date.now() - startTime,
        retryCount: retryConfig.count,
        finalError: err.message,
      }

      consola.error('🔴 All retries failed:', errorContext)
      posthog.capture('grpc_all_retries_failed', errorContext)
      return throwError(() => err)
    }),
  )
}

/**
 * Get user by ID.
 * @example
 {
 "ref_id_user": "01929ca0_8af9_7da9_9e8d_fcdc72513047"
 }
 * @param userGetRequest
 */

export function cmwUserGetRequest(userGetRequest: UserGetRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcUseruser_getDesc, userGetRequest)
}

/**
 * Update user by ID.
 * @param userUpdateRequest
 */
export function cmwUserUpdateRequest(userUpdateRequest: UserUpdateRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcUseruser_updateDesc, userUpdateRequest)
}

/**
 * Delete user by ID.
 * @param userDeleteRequest
 */
export function cmwUserDeleteRequest(userDeleteRequest: UserDeleteRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcUseruser_deleteDesc, userDeleteRequest)
}

/**
 * Get organization by ID.
 * @example
 {
 "ref_id_org": "01JAF8DQHD29XEB6A3VMGWJAT8",
 "ref_id_user": "01929ca0_8af9_7da9_9e8d_fcdc72513047"
 }
 * @param orgGetRequest
 */
export function cmwOrgGetRequest(orgGetRequest: OrgGetRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOrgorg_getDesc, orgGetRequest)
}

/**
 * Get organization list.
 * @param orgListRequest
 */
export function cmwOrgListRequest(orgListRequest: OrgListRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOrgorg_listDesc, orgListRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_FOUND])
}

/**
 * Create organization and associate with the provided user and sets the provided user as the admin.
 * @param orgCreateRequest
 */
export function cmwOrgCreateRequest(orgCreateRequest: OrgCreateRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOrgorg_createDesc, orgCreateRequest)
}

/**
 * Create company and associate with the provided organization and sets the provided user as the admin.
 * @param compCreateRequest
 */
export function cmwCompCreateRequest(compCreateRequest: CompCreateRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompcomp_createDesc, compCreateRequest)
}

/**
 * Update company and associate with the provided organization and sets the provided user as the admin.
 * @param compUpdateRequest
 */
export function cmwCompUpdateRequest(compUpdateRequest: CompUpdateRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompcomp_updateDesc, compUpdateRequest)
}

/**
 * Get company by ID.
 * @param compGetRequest
 */
export function cmwCompGetRequest(compGetRequest: CompGetRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompcomp_getDesc, compGetRequest)
}

/**
 * Get company list by org.
 * @param compByUserListRequest
 */
export function cmwCompByUserListRequest(compByUserListRequest: CompByUserListRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompcomp_by_user_listDesc, compByUserListRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_FOUND])
}

/**
 * Get company list by org.
 * @param compByOrgListRequest
 */
export function cmwCompByOrgListRequest(compByOrgListRequest: CompByOrgListRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompcomp_by_org_listDesc, compByOrgListRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_FOUND])
}

/**
 * Delete company and associated users.
 * @param compDelRequest
 */
export function cmwCompDelRequest(compDelRequest: CompDelRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompcomp_deleteDesc, compDelRequest)
}

/**
 * Upload company account data.
 * @param dmCompAccDataUploadRequest
 */
export function cmwDmCompAccDataUploadRequest(dmCompAccDataUploadRequest: DmCompAccDataUploadRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_comp_acc_data_uploadDesc, dmCompAccDataUploadRequest)
}

/**
 * Confirm company account data upload.
 * @param dmCompAccDataUploadConfirmRequest
 */
export function cmwDmCompAccDataUploadConfirmRequest(dmCompAccDataUploadConfirmRequest: DmCompAccDataUploadConfirmRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_comp_acc_data_upload_confirmDesc, dmCompAccDataUploadConfirmRequest)
}

/**
 * Get actuals res fstmt for the company.
 * @param compFssFlatGetRequest
 */
export function cmwCompFssFlatGetRequest(compFssFlatGetRequest: CompFssFlatGetRequest): Observable<GenericPayloadResponse> {
  consola.debug('🔵 compFssFlatGetRequest: ', compFssFlatGetRequest)
  return callCmwRequest(StcsWappSvcCompFinancialscomp_fss_flat_getDesc, compFssFlatGetRequest)
}

/**
 * Get actuals res values for the company.
 * @param compActualsResValuesGetRequest
 */
export function cmwCompActualsResValuesGetRequest(compActualsResValuesGetRequest: CompActualsResValuesGetRequest): Observable<GenericPayloadResponse> {
  consola.debug('🔵 cmwGetActualsResValuesRequest: ', compActualsResValuesGetRequest)
  return callCmwRequest(StcsWappSvcCompFinancialscomp_actuals_res_values_getDesc, compActualsResValuesGetRequest)
}

/**
 * Update the organization.
 * @param orgUpdateRequest
 */
export function cmwOrgUpdateRequest(orgUpdateRequest: OrgUpdateRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOrgorg_updateDesc, orgUpdateRequest)
}

/**
 * Get user list on the ref_comp_map for the organization.
 * @param orgUserListRequest
 */
export function cmwOrgUserListRequest(orgUserListRequest: OrgUserListRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOrgorg_user_listDesc, orgUserListRequest)
}

/**
 * Update user list on the ref_comp_map for the organization.
 * @param orgUserUpdateAccessRequest
 */
export function cmwOrgUserUpdateAccessRequest(orgUserUpdateAccessRequest: OrgUserUpdateAccessRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOrgorg_user_update_accessDesc, orgUserUpdateAccessRequest)
}

/**
 * Adds and invites a user to the org. The user will be added to the org and companies as defined in the request.
 * @redirect_url: The URL to redirect the user to after accepting the invitation. Please use the current window location to redirect the user.
 * @param orgUserAddRequest
 */
export function cmwOrgUserAddRequest(orgUserAddRequest: OrgUserAddRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOrgorg_user_addDesc, orgUserAddRequest)
}

/**
 * Deletes a user from the org. The user will be removed from the org and companies as defined in the request.
 * @param orgUserDelRequest
 */
export function cmwOrgUserDelRequest(orgUserDelRequest: OrgUserDelRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOrgorg_user_delDesc, orgUserDelRequest)
}

/**
 * Get FSD flat for the organization.
 * @param dmOrgFsdFlatGetRequest
 */
export function cmwDmOrgFsdFlatGetRequest(dmOrgFsdFlatGetRequest: DmOrgFsdFlatGetRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_org_fsd_flat_getDesc, dmOrgFsdFlatGetRequest)
}

/**
 * Get draft FSD flat for the organization.
 * @param dmOrgFsdFlatGetDraftRequest
 */
export function cmwDmOrgFsdFlatGetDraftRequest(dmOrgFsdFlatGetDraftRequest: DmOrgFsdFlatGetDraftRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_org_fsd_flat_get_draftDesc, dmOrgFsdFlatGetDraftRequest)
}

/**
 * Preview FSD flat for the organization.
 * @param dmOrgFsdFlatPreviewRequest
 */
export function cmwDmOrgFsdFlatPreviewRequest(dmOrgFsdFlatPreviewRequest: DmOrgFsdFlatPreviewRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_org_fsd_flat_previewDesc, dmOrgFsdFlatPreviewRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED, StcsResCode.NOT_FOUND])
}

/**
 * Restore FSD flat for the organization.
 * @param dmOrgFsdFlatRestoreRequest
 */
export function cmwDmOrgFsdFlatRestoreRequest(dmOrgFsdFlatRestoreRequest: DmOrgFsdFlatRestoreRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_org_fsd_flat_restoreDesc, dmOrgFsdFlatRestoreRequest)
}

/**
 * Create a planCfg for the comp.
 * @param compPlanCfgCreateRequest
 */
export function cmwCompPlanCfgCreateRequest(compPlanCfgCreateRequest: CompPlanCfgCreateRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompPlancomp_plan_cfg_createDesc, compPlanCfgCreateRequest)
}

/**
 * Create a planCfg for the comp.
 * @param compPlanCfgDeactivateRequest
 */
export function cmwCompPlanCfgDeactivateRequest(compPlanCfgDeactivateRequest: CompPlanCfgDeactivateRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompPlancomp_plan_cfg_deactivateDesc, compPlanCfgDeactivateRequest)
}

/**
 * Delete a planCfg for the comp.
 * @param compPlanCfgDelRequest
 */
export function cmwCompPlanCfgDelRequest(compPlanCfgDelRequest: CompPlanCfgDelRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompPlancomp_plan_cfg_deleteDesc, compPlanCfgDelRequest)
}

/**
 * Metadata and i18n modification methods, no plan cfg content will be modified
 * Required fields: ref_id_user, ref_id_comp, ref_id_org, ref_id_comp_plan_cfg
 * @param compPlanCfgSaveRequest
 */
export function cmwCompPlanCfgSaveRequest(compPlanCfgSaveRequest: CompPlanCfgSaveRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompPlancomp_plan_cfg_saveDesc, compPlanCfgSaveRequest)
}

/**
 * Calculation method for plan values -> EDITOR ONLY
 * Required fields: ref_id_user, ref_id_comp, ref_id_org, ref_id_comp_plan_cfg, plan_cfg
 * @param compPlanCfgCalcRequest
 */
export function cmwCompPlanCfgCalcRequest(compPlanCfgCalcRequest: CompPlanCfgCalcRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompPlancomp_plan_cfg_calcDesc, compPlanCfgCalcRequest)
}

/**
 * Activate method for activating and creating pla_res_values -> OUTSIDE EDITOR ONLY
 * @param compPlanCfgActivateRequest
 */
export function cmwCompPlanCfgActivateRequest(compPlanCfgActivateRequest: CompPlanCfgActivateRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompPlancomp_plan_cfg_activateDesc, compPlanCfgActivateRequest)
}

/**
 * Get a planCfg for the comp.
 * @param compPlanCfgGetRequest
 */
export function cmwCompPlanCfgGetRequest(compPlanCfgGetRequest: CompPlanCfgGetRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompPlancomp_plan_cfg_getDesc, compPlanCfgGetRequest)
}

/**
 * Get a list of planCfg for the comp.
 * @param compPlanCfgListRequest
 */
export function cmwCompPlanCfgListRequest(compPlanCfgListRequest: CompPlanCfgListRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompPlancomp_plan_cfg_listDesc, compPlanCfgListRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_FOUND])
}

/**
 * Sort the company list by the org.
 * @param orgCompSortListRequest
 */
export function cmwOrgCompSortListRequest(orgCompSortListRequest: OrgCompSortListRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOrgorg_comp_sort_listDesc, orgCompSortListRequest)
}

/**
 * Initial account data upload request which creates comp and uploads AccData.
 * @param onboardingCompleteDataUploadRequest
 */
export function cmwOnboardingCompleteDataUploadRequest(onboardingCompleteDataUploadRequest: DmCompAccDataUploadRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOnboardingonboarding_complete_data_uploadDesc, onboardingCompleteDataUploadRequest)
}

/**
 * Initial FSD flat upload request which creates comp and uploads FSD flat.
 * @param onboardingCompleteFsdRequest
 */
export function cmwOnboardingCompleteFsdRequest(onboardingCompleteFsdRequest: OnboardingCompleteFsdRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOnboardingonboarding_complete_fsdDesc, onboardingCompleteFsdRequest)
}

/**
 * Initial account data upload request which creates comp and uploads AccData.
 * @param onboardingCompleteAccMappingRequest
 */
export function cmwOnboardingCompleteAccMappingRequest(onboardingCompleteAccMappingRequest: OnboardingCompleteAccMappingRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOnboardingonboarding_complete_acc_mappingDesc, onboardingCompleteAccMappingRequest)
}

/**
 * Initial account data upload request which creates comp and uploads AccData.
 * @param onboardingPauseRequest
 */
export function cmwOnboardingPauseRequest(onboardingPauseRequest: OnboardingPauseRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOnboardingonboarding_pauseDesc, onboardingPauseRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED, StcsResCode.NOT_FOUND])
}

/**
 * Sends the onboarding complete request to the specified service.
 *
 * @param {OnboardingCompleteRequest} onboardingCompleteRequest - The request object containing the details required to complete the onboarding process.
 * @return {Observable<MultiGenericPayloadResponse>} An observable that emits the response payload upon completing the request.
 */
export function cmwOnboardingCompleteRequest(onboardingCompleteRequest: OnboardingCompleteRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOnboardingonboarding_completeDesc, onboardingCompleteRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED])
}

/**
 * Initial account data upload request which creates comp and uploads AccData.
 * @param onboardingResumeRequest
 */
export function cmwOnboardingResumeRequest(onboardingResumeRequest: OnboardingResumeRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOnboardingonboarding_resumeDesc, onboardingResumeRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED, StcsResCode.NOT_FOUND])
}

/**
 * Update FSD flat for the organization.
 * @param dmOrgFsdFlatSaveDraftRequest
 */
export function cmwDmOrgFsdFlatSaveDraftRequest(dmOrgFsdFlatSaveDraftRequest: DmOrgFsdFlatSaveDraftRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_org_fsd_flat_save_draftDesc, dmOrgFsdFlatSaveDraftRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED, StcsResCode.NOT_FOUND])
}

/**
 * Confirm FSD flat for the organization.
 * @param dmOrgFsdFlatConfirmRequest
 */
export function cmwDmOrgFsdFlatConfirmRequest(dmOrgFsdFlatConfirmRequest: DmOrgFsdFlatConfirmRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_org_fsd_flat_confirmDesc, dmOrgFsdFlatConfirmRequest)
}

/**
 * Confirm company account mapping.
 * @param dmCompAcmConfirmRequest
 */
export function cmwDmCompAcmConfirmRequest(dmCompAcmConfirmRequest: DmCompAcmConfirmRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_comp_acm_confirmDesc, dmCompAcmConfirmRequest)
}

/**
 * Get company account mapping.
 * @param dmCompAcmGetRequest
 */
export function cmwDmCompAcmGetRequest(dmCompAcmGetRequest: DmCompAcmGetRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_comp_acm_getDesc, dmCompAcmGetRequest)
}

/**
 * Get draft company account mapping.
 * @param dmCompAcmGetDraftRequest
 */
export function cmwDmCompAcmGetDraftRequest(dmCompAcmGetDraftRequest: DmCompAcmGetDraftRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_comp_acm_get_draftDesc, dmCompAcmGetDraftRequest)
}

/**
 * Preview company account mapping.
 * @param dmCompAcmPreviewRequest
 */
export function cmwDmCompAcmPreviewRequest(dmCompAcmPreviewRequest: DmCompAcmPreviewRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_comp_acm_previewDesc, dmCompAcmPreviewRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED, StcsResCode.NOT_FOUND])
}

/**
 * Restore company account mapping.
 * @param dmCompAcmRestoreRequest
 */
export function cmwDmCompAcmRestoreRequest(dmCompAcmRestoreRequest: DmCompAcmRestoreRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_comp_acm_restoreDesc, dmCompAcmRestoreRequest)
}

/**
 * Update company account mapping.
 * @param dmCompAcmSaveDraftRequest
 */
export function cmwDmCompAcmSaveDraftRequest(dmCompAcmSaveDraftRequest: DmCompAcmSaveDraftRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_comp_acm_save_draftDesc, dmCompAcmSaveDraftRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED, StcsResCode.NOT_FOUND])
}

/**
 * Get company plan res values.
 * @param compPlanResValuesGetRequest
 */
export function cmwCompPlanResValuesGetRequest(compPlanResValuesGetRequest: CompPlanResValuesGetRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompPlancomp_plan_res_values_getDesc, compPlanResValuesGetRequest)
}

/** Get company plan rolling-forecast (rf) res values.
 * @param compPlanRfGetRequest
 */
export function cmwCompPlanRfGetRequest(compPlanRfGetRequest: CompPlanRfGetRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompPlancomp_plan_rf_res_values_getDesc, compPlanRfGetRequest)
}

/**
 * Get company data overview.
 * @param dmCompDataOverViewRequest
 */
export function cmwDmCompDataOverViewRequest(dmCompDataOverViewRequest: DmCompDataOverViewRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_comp_data_overviewDesc, dmCompDataOverViewRequest)
}

/**
 * Delete company actuals.
 * @param dmCompDeleteActualsRequest
 */
export function cmwDmCompDeleteActualsRequest(dmCompDeleteActualsRequest: DmCompDeleteActualsRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_comp_delete_actualsDesc, dmCompDeleteActualsRequest)
}

/**
 * Get FSD template list for the organization.
 * @param dmOrgFsdTemplateListRequest
 */
export function cmwDmOrgFsdTemplateListRequest(dmOrgFsdTemplateListRequest: DmOrgFsdTemplateListRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_org_fsd_template_listDesc, dmOrgFsdTemplateListRequest)
}

/**
 * Apply FSD template for the organization.
 * @param dmOrgFsdTemplateApplyRequest
 */
export function cmwDmOrgFsdTemplateApplyRequest(dmOrgFsdTemplateApplyRequest: DmOrgFsdTemplateApplyRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcDataManagementdm_org_fsd_template_applyDesc, dmOrgFsdTemplateApplyRequest)
}

/**
 * Confirm a welcome request for onboarding.
 * @param onboardingConfirmWelcomeRequest
 */
export function cmwOnboardingConfirmWelcomeRequest(onboardingConfirmWelcomeRequest: OnboardingConfirmWelcomeRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcOnboardingonboarding_confirm_welcomeDesc, onboardingConfirmWelcomeRequest)
}

/**
 * Get organization Wapp settings.
 * @param getWappSettingsRequest
 */
export function cmwGetOrgWappSettingsRequest(getWappSettingsRequest: GetWappSettingsRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcSettingsget_org_wapp_settingsDesc, getWappSettingsRequest)
}

/**
 * Update organization Wapp settings.
 * @param updateOrgWappSettingsRequest
 */
export function cmwUpdateOrgWappSettingsRequest(updateOrgWappSettingsRequest: UpdateOrgWappSettingsRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcSettingsupdate_org_wapp_settingsDesc, updateOrgWappSettingsRequest)
}

/**
 * Update company Wapp settings.
 * @param updateCompWappSettingsRequest
 */
export function cmwUpdateCompWappSettingsRequest(updateCompWappSettingsRequest: UpdateCompWappSettingsRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcSettingsupdate_comp_wapp_settingsDesc, updateCompWappSettingsRequest)
}

/**
 * Update user Wapp settings.
 * @param updateCompUserWappSettingsRequest
 */
export function cmwUpdateCompUserWappSettingsRequest(updateCompUserWappSettingsRequest: UpdateCompUserWappSettingsRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcSettingsupdate_comp_user_wapp_settingsDesc, updateCompUserWappSettingsRequest)
}

/**
 * Create a KPI configuration for the company.
 * @param compKpiCfgCreateRequest
 */
export function cmwCompKpiCfgCreateRequest(compKpiCfgCreateRequest: CompKpiCfgCreateRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompKpicomp_kpi_cfg_createDesc, compKpiCfgCreateRequest)
}

/**
 * Get a KPI config for the company.
 * @param compKpiCfgCalcRequest
 */
export function cmwCompKpiCfgCalcRequest(compKpiCfgCalcRequest: CompKpiCfgCalcRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompKpicomp_kpi_cfg_calcDesc, compKpiCfgCalcRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED, StcsResCode.NOT_FOUND])
}

/**
 * Get company KPI result values.
 * @param compKpiResValuesGetRequest
 */
export function cmwCompKpiResValuesGetRequest(compKpiResValuesGetRequest: CompKpiResValuesGetRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompKpicomp_kpi_res_values_getDesc, compKpiResValuesGetRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED, StcsResCode.NOT_FOUND])
}

/**
 * Check payment status for a given Organization.
 * Returns the stcs_org entity with the updated subscription status and seat details.
 * @param orgPaymentStatusRequest
 */
export function cmwOrgPaymentStatusRequest(orgPaymentStatusRequest: OrgPaymentStatusRequest): Observable<MultiGenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcPaymentorg_check_payment_statusDesc, orgPaymentStatusRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED, StcsResCode.NOT_FOUND])
}

/**
 * Update company i18n map.
 * Return the updated stcs_comp entity.
 * @param compUpdateI18nMapRequest
 */
export function cmwCompUpdateI18nMapRequest(compUpdateI18nMapRequest: CompUpdateI18nMapRequest): Observable<GenericPayloadResponse> {
  return callCmwRequest(StcsWappSvcCompcomp_update_i18n_mapDesc, compUpdateI18nMapRequest, [StcsResCode.SUCCESS, StcsResCode.NOT_AUTHORIZED, StcsResCode.NOT_FOUND])
}
